import { useCallback, useLayoutEffect, useRef, useSyncExternalStore } from "react";
import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePreferences } from "@/contexts/PreferencesContext";
import { useProfile } from "@/hooks/useProfile";
import { profileService, type UserProfile } from "@/services/profile.service";
import {
  HOME_PAGE, LAYOUT_KEY, START_PAGE_KEY, confirmsLayout, layoutForProfile, parseLayout, patchProfileLayout,
  resolveStartPage, type HomeLayout, type PlaceablePage,
} from "./homeLayout";

// Only the start page belongs to this device. Lists use the existing profile cache.
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => { listeners.delete(listener); window.removeEventListener("storage", listener); };
};
const readStartPage = (): string | null => {
  try { return localStorage.getItem(START_PAGE_KEY); } catch { return null; }
};
const readLegacyLayout = (): string | null => {
  try { return localStorage.getItem(LAYOUT_KEY); } catch { return null; }
};
const writeStartPage = (value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(START_PAGE_KEY);
    else localStorage.setItem(START_PAGE_KEY, value);
  } catch { /* Device storage may be unavailable. */ }
  listeners.forEach(listener => listener());
};
const readNothing = () => null;
export const storedStartPage = (): PlaceablePage | null => resolveStartPage(readStartPage());
const mutationKey = ["profile", "home-layout"];

export function useHomeLayout() {
  const { user, isAuthenticated } = useAuth();
  const { isLoading: preferencesLoading } = usePreferences();
  const userId = isAuthenticated ? user?.user_id ?? null : null;
  const activeUser = useRef(userId);
  useLayoutEffect(() => { activeUser.current = userId; }, [userId]);
  const queryClient = useQueryClient();
  const profileQuery = useProfile();
  const storedStart = useSyncExternalStore(subscribe, readStartPage, readNothing);
  const legacy = useSyncExternalStore(subscribe, readLegacyLayout, readNothing);
  const layout = layoutForProfile(profileQuery.data, userId, legacy);
  const startPage = resolveStartPage(storedStart);
  const pending = useIsMutating({ mutationKey }) > 0;

  const mutation = useMutation({
    mutationKey,
    scope: { id: "profile-home-layout" },
    mutationFn: async ({ next, owner }: { next: HomeLayout | null; owner: string }) => {
      if (activeUser.current !== owner) throw new Error("Account changed");
      const updated = await profileService.updateProfile({ home_layout: next });
      if (!confirmsLayout(updated, owner, next)) throw new Error("Server did not confirm the layout");
      return updated;
    },
    onMutate: async ({ next, owner }) => {
      await queryClient.cancelQueries({ queryKey: ["profile"], exact: true });
      const profile = queryClient.getQueryData<UserProfile>(["profile"]);
      if (activeUser.current !== owner || profile?.user_id !== owner) throw new Error("Account changed");
      const previous = profile.home_layout;
      queryClient.setQueryData<UserProfile>(["profile"], current => patchProfileLayout(current, owner, next));
      return { previous };
    },
    onSuccess: async (updated, { owner }) => {
      if (activeUser.current !== owner) return;
      await queryClient.cancelQueries({ queryKey: ["profile"], exact: true });
      if (activeUser.current !== owner) return;
      queryClient.setQueryData<UserProfile>(["profile"], current => patchProfileLayout(current, owner, updated.home_layout));
    },
    onError: (_error, { owner }, context) => {
      if (activeUser.current !== owner) return;
      if (context) queryClient.setQueryData<UserProfile>(["profile"], current => patchProfileLayout(current, owner, context.previous));
      toast.error("Failed to save preference");
    },
  });

  const isLoading = preferencesLoading || profileQuery.isFetching || !userId || profileQuery.data?.user_id !== userId;
  const save = async (next: HomeLayout | null): Promise<boolean> => {
    if (isLoading || !userId || queryClient.isMutating({ mutationKey })) return false;
    try { await mutation.mutateAsync({ next, owner: userId }); return activeUser.current === userId; }
    catch { return false; } // Mutation owns rollback and the visible error.
  };
  const setLayout = (next: HomeLayout) => save(next);
  const setStartPage = useCallback((page: PlaceablePage | null) => writeStartPage(page ?? HOME_PAGE), []);
  const reset = async () => { if (await save(parseLayout(null))) writeStartPage(null); };
  const refresh = profileQuery.refetch;

  return {
    layout, startPage, setLayout, setStartPage, reset, refresh,
    isCustomized: (profileQuery.data?.user_id === userId && profileQuery.data.home_layout != null) || storedStart !== null,
    isSaving: pending, isLoading, loadError: profileQuery.isError,
  };
}
